import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, theme, getPaletteColor } from 'pcln-design-system'
import Day from './Day'
import DayNames from './DayNames'
import { DayMapContext } from './Calendar'
import { getLocalized } from './helpers/locales.js'
import { format } from 'date-fns'
import deprecatedPropType from './helpers/deprecatedPropType/deprecatedPropType'

const now = format(Date.now(), 'yyyyMMdd')
const MonthName = styled(Box)`
  font-size: ${(props) => props.theme.fontSizes[2]}px;
  font-weight: ${(props) => props.theme.bold};
  color: ${getPaletteColor('text.light')};
  background: ${getPaletteColor('background.light')};
  text-align: center;
  text-transform: capitalize;

  ${(props) => props.theme.mediaQueries.md} {
    color: ${getPaletteColor('text.dark')};
    background: ${getPaletteColor('background.lightest')};
  }
`

const MonthBox = styled(Box)`
  ${(props) => props.theme.mediaQueries.md} {
    vertical-align: top;
    display: inline-block;
  }
`

const stringOrNumber = PropTypes.oneOfType([PropTypes.string, PropTypes.number])

class Month extends React.Component {
  static propTypes = {
    startDate: stringOrNumber,
    endDate: stringOrNumber,
    minDate: stringOrNumber,
    maxDate: stringOrNumber,
    maxSelectableDate: PropTypes.string,
    displayYear: PropTypes.bool,
    startsWithSunday: deprecatedPropType('locale'),
    showHoveredDates: PropTypes.bool,
    onDayClicked: PropTypes.func,
    onDayHovered: PropTypes.func,
    dayWrapperStyleOverride: PropTypes.object,
    monthWidth: PropTypes.number,
    highlightedDates: PropTypes.array,
    hoveredDate: PropTypes.string,
    monthIndex: PropTypes.number.isRequired,
    year: PropTypes.string,
    days: PropTypes.array.isRequired,
    labels: PropTypes.array,
    isTouch: PropTypes.bool,
    setSelectedDayRef: PropTypes.func,
    didUseKeyNavigation: PropTypes.bool,
    locale: PropTypes.string,
  }

  static defaultProps = {
    monthWidth: 380,
    theme,
    startDate: now,
    minDate: now,
    maxDate: now,
    endDate: now,
  }

  render() {
    const {
      startDate,
      endDate,
      minDate,
      maxDate,
      maxSelectableDate,
      displayYear,
      startsWithSunday,
      showHoveredDates,
      onDayClicked,
      onDayHovered,
      dayWrapperStyleOverride,
      monthWidth,
      highlightedDates,
      hoveredDate,
      monthIndex,
      year,
      days,
      labels,
      isTouch,
      didUseKeyNavigation,
      locale,
    } = this.props
    const requestedLocale = getLocalized(locale)

    const months = (format) => {
      // valid formats are:
      // narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
      // abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      // wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const returnValue = []
      for (let m = 0; m < 12; m++) {
        returnValue.push(requestedLocale.month(m, { width: format }))
      }
      return returnValue
    }

    return (
      <MonthBox width={[1, 1, monthWidth]} p={[0, 0, 3]}>
        <MonthName pb={[1, 2, 2]} pt={[1, 2, 0]}>
          {months('wide')[monthIndex]}
          {displayYear && ` ${year}`}
        </MonthName>
        <DayNames startsWithSunday={startsWithSunday} locale={locale} />
        <DayMapContext.Consumer>
          {(priceContext) => (
            <div>
              {days.map((fullDate, i) => (
                <Day
                  ariaLabel={(labels && labels[i]) || ''}
                  endDate={endDate}
                  fullDate={fullDate}
                  highlightedDates={highlightedDates}
                  hoveredDate={hoveredDate}
                  maxDate={maxDate}
                  onDayClicked={onDayClicked}
                  onDayHovered={onDayHovered}
                  dayWrapperStyleOverride={dayWrapperStyleOverride}
                  startDate={startDate}
                  startsWithSunday={startsWithSunday}
                  locale={locale}
                  minDate={minDate}
                  maxSelectableDate={maxSelectableDate}
                  showHoveredDates={showHoveredDates}
                  isTouch={isTouch}
                  key={`datepicker-day-${fullDate || `${monthIndex}${i}`}`}
                  customDayProps={priceContext && priceContext.dayMap ? priceContext.dayMap[fullDate] : null}
                  renderDayFn={priceContext && priceContext.renderDayFn}
                  didUseKeyNavigation={didUseKeyNavigation}
                  setSelectedDayRef={this.props.setSelectedDayRef}
                />
              ))}
            </div>
          )}
        </DayMapContext.Consumer>
      </MonthBox>
    )
  }
}

export default Month
